/**
 * Cliente API normalizado que automáticamente aplica normalización a todas las respuestas
 */

import apiClient from './apiClient';
import { normalizeApiResponse, normalizeDataObject, normalizeDataArray } from '../utils/normalizationUtils';

/**
 * Wrapper que aplica normalización automática a las respuestas de la API
 */
class NormalizedApiClient {
  /**
   * GET request con normalización automática
   */
  async get(url, entityType = 'generic', options = {}) {
    try {
      const response = await apiClient.get(url, options);
      
      // Aplicar normalización según el tipo de entidad
      if (response.data) {
        const normalizedData = normalizeApiResponse(response.data, entityType);
        return {
          ...response,
          data: normalizedData
        };
      }
      
      return response;
    } catch (error) {
      console.error(`Error en GET ${url}:`, error);
      throw error;
    }
  }

  /**
   * POST request con normalización automática
   */
  async post(url, data, entityType = 'generic', options = {}) {
    try {
      const response = await apiClient.post(url, data, options);
      
      // Aplicar normalización a la respuesta
      if (response.data) {
        const normalizedData = normalizeApiResponse(response.data, entityType);
        return {
          ...response,
          data: normalizedData
        };
      }
      
      return response;
    } catch (error) {
      console.error(`Error en POST ${url}:`, error);
      throw error;
    }
  }

  /**
   * PUT request con normalización automática
   */
  async put(url, data, entityType = 'generic', options = {}) {
    try {
      const response = await apiClient.put(url, data, options);
      
      // Aplicar normalización a la respuesta
      if (response.data) {
        const normalizedData = normalizeApiResponse(response.data, entityType);
        return {
          ...response,
          data: normalizedData
        };
      }
      
      return response;
    } catch (error) {
      console.error(`Error en PUT ${url}:`, error);
      throw error;
    }
  }

  /**
   * DELETE request con normalización automática
   */
  async delete(url, entityType = 'generic', options = {}) {
    try {
      const response = await apiClient.delete(url, options);
      
      // Aplicar normalización a la respuesta si existe
      if (response.data) {
        const normalizedData = normalizeApiResponse(response.data, entityType);
        return {
          ...response,
          data: normalizedData
        };
      }
      
      return response;
    } catch (error) {
      console.error(`Error en DELETE ${url}:`, error);
      throw error;
    }
  }

  /**
   * PATCH request con normalización automática
   */
  async patch(url, data, entityType = 'generic', options = {}) {
    try {
      const response = await apiClient.patch(url, data, options);
      
      // Aplicar normalización a la respuesta
      if (response.data) {
        const normalizedData = normalizeApiResponse(response.data, entityType);
        return {
          ...response,
          data: normalizedData
        };
      }
      
      return response;
    } catch (error) {
      console.error(`Error en PATCH ${url}:`, error);
      throw error;
    }
  }
}

// Crear instancia singleton
const normalizedApiClient = new NormalizedApiClient();

/**
 * Funciones helper para llamadas específicas por entidad
 */
export const apiHelpers = {
  // Empresas
  empresas: {
    get: (url, options = {}) => normalizedApiClient.get(url, 'empresa', options),
    post: (url, data, options = {}) => normalizedApiClient.post(url, data, 'empresa', options),
    put: (url, data, options = {}) => normalizedApiClient.put(url, data, 'empresa', options),
    delete: (url, options = {}) => normalizedApiClient.delete(url, 'empresa', options),
  },
  
  // Usuarios
  usuarios: {
    get: (url, options = {}) => normalizedApiClient.get(url, 'usuario', options),
    post: (url, data, options = {}) => normalizedApiClient.post(url, data, 'usuario', options),
    put: (url, data, options = {}) => normalizedApiClient.put(url, data, 'usuario', options),
    delete: (url, options = {}) => normalizedApiClient.delete(url, 'usuario', options),
  },
  
  // Clientes
  clientes: {
    get: (url, options = {}) => normalizedApiClient.get(url, 'cliente', options),
    post: (url, data, options = {}) => normalizedApiClient.post(url, data, 'cliente', options),
    put: (url, data, options = {}) => normalizedApiClient.put(url, data, 'cliente', options),
    delete: (url, options = {}) => normalizedApiClient.delete(url, 'cliente', options),
  },
  
  // Vehículos
  vehiculos: {
    get: (url, options = {}) => normalizedApiClient.get(url, 'vehiculo', options),
    post: (url, data, options = {}) => normalizedApiClient.post(url, data, 'vehiculo', options),
    put: (url, data, options = {}) => normalizedApiClient.put(url, data, 'vehiculo', options),
    patch: (url, data, options = {}) => normalizedApiClient.patch(url, data, 'vehiculo', options),
    delete: (url, options = {}) => normalizedApiClient.delete(url, 'vehiculo', options),
  },
  
  // Citas
  citas: {
    get: (url, options = {}) => normalizedApiClient.get(url, 'cita', options),
    post: (url, data, options = {}) => normalizedApiClient.post(url, data, 'cita', options),
    put: (url, data, options = {}) => normalizedApiClient.put(url, data, 'cita', options),
    delete: (url, options = {}) => normalizedApiClient.delete(url, 'cita', options),
  },
  
  // Servicios
  servicios: {
    get: (url, options = {}) => normalizedApiClient.get(url, 'servicio', options),
    post: (url, data, options = {}) => normalizedApiClient.post(url, data, 'servicio', options),
    put: (url, data, options = {}) => normalizedApiClient.put(url, data, 'servicio', options),
    patch: (url, data, options = {}) => normalizedApiClient.patch(url, data, 'servicio', options),
    delete: (url, options = {}) => normalizedApiClient.delete(url, 'servicio', options),
  },
  
  // Repuestos
  repuestos: {
    get: (url, options = {}) => normalizedApiClient.get(url, 'repuesto', options),
    post: (url, data, options = {}) => normalizedApiClient.post(url, data, 'repuesto', options),
    put: (url, data, options = {}) => normalizedApiClient.put(url, data, 'repuesto', options),
    delete: (url, options = {}) => normalizedApiClient.delete(url, 'repuesto', options),
  },
  
  // Mecánicos
  mecanicos: {
    get: (url, options = {}) => normalizedApiClient.get(url, 'mecanico', options),
    post: (url, data, options = {}) => normalizedApiClient.post(url, data, 'mecanico', options),
    put: (url, data, options = {}) => normalizedApiClient.put(url, data, 'mecanico', options),
    patch: (url, data, options = {}) => normalizedApiClient.patch(url, data, 'mecanico', options),
    delete: (url, options = {}) => normalizedApiClient.delete(url, 'mecanico', options),
  },
  
  // Sucursales
  sucursales: {
    get: (url, options = {}) => normalizedApiClient.get(url, 'sucursal', options),
    post: (url, data, options = {}) => normalizedApiClient.post(url, data, 'sucursal', options),
    put: (url, data, options = {}) => normalizedApiClient.put(url, data, 'sucursal', options),
    delete: (url, options = {}) => normalizedApiClient.delete(url, 'sucursal', options),
  },
  
  // Generic
  generic: {
    get: (url, options = {}) => normalizedApiClient.get(url, 'generic', options),
    post: (url, data, options = {}) => normalizedApiClient.post(url, data, 'generic', options),
    put: (url, data, options = {}) => normalizedApiClient.put(url, data, 'generic', options),
    delete: (url, options = {}) => normalizedApiClient.delete(url, 'generic', options),
  }
};

export default normalizedApiClient;